import React, { useState } from 'react';
import { apiService } from '../../services/apiService';
import { notificationService } from '../../services/notificationService';

interface RegisterProps {
  onRegisterSuccess: (user: any) => void;
  onSwitchToLogin: () => void;
}

const Register: React.FC<RegisterProps> = ({ onRegisterSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Calculate password strength
    if (name === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password: string) => {
    let strength = 0;
    
    if (password.length >= 8) strength += 1;
    if (password.match(/[a-z]/)) strength += 1;
    if (password.match(/[A-Z]/)) strength += 1;
    if (password.match(/[0-9]/)) strength += 1;
    if (password.match(/[^a-zA-Z0-9]/)) strength += 1;
    
    setPasswordStrength(strength);
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return '#ef4444';
      case 2:
        return '#f59e0b';
      case 3:
        return '#eab308';
      case 4:
        return '#22c55e';
      case 5:
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Strong';
      case 5:
        return 'Very Strong';
      default:
        return '';
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.username || !formData.password) {
      notificationService.warning('Please fill in all required fields');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      notificationService.warning('Passwords do not match');
      return false;
    }

    if (formData.password.length < 8) {
      notificationService.warning('Password must be at least 8 characters long');
      return false;
    }

    if (formData.username.length < 3) {
      notificationService.warning('Username must be at least 3 characters long');
      return false;
    }

    if (formData.username.length > 50) {
      notificationService.warning('Username must be less than 50 characters');
      return false;
    }

    // Check username format - only letters, numbers, hyphens, and underscores
    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      notificationService.warning('Username can only contain letters, numbers, hyphens, and underscores');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const registerData = {
        email: formData.email,
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name || null
      };

      const response = await apiService.register(registerData);
      
      notificationService.success('Account created successfully! Please check your email to verify your account.');
      onRegisterSuccess(response);
      
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      notificationService.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    try {
      const response = await apiService.get('/auth/google/login');
      window.location.href = response.auth_url;
    } catch (error) {
      console.error('Google register error:', error);
      notificationService.error('Failed to initiate Google registration');
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
          .register-container {
            animation: fadeInUp 0.8s ease-out;
          }
          .floating-icon {
            animation: float 3s ease-in-out infinite;
          }
          .strength-indicator {
            transition: all 0.3s ease;
          }
        `}
      </style>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '1rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: '120px',
          height: '120px',
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          animation: 'float 5s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          left: '8%',
          width: '90px',
          height: '90px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite reverse'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '40%',
          right: '5%',
          width: '60px',
          height: '60px',
          background: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite'
        }}></div>

        <div className="register-container" style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          padding: '3rem 2.5rem',
          width: '100%',
          maxWidth: '520px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          position: 'relative'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div className="floating-icon" style={{ 
              marginBottom: '1.5rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
              position: 'relative'
            }}>
              <i className="bi bi-person-plus" style={{ fontSize: '2.5rem', color: 'white' }}></i>
              <div style={{
                position: 'absolute',
                inset: '-2px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                borderRadius: '22px',
                zIndex: -1,
                opacity: 0.3,
                filter: 'blur(8px)'
              }}></div>
            </div>
            <h1 style={{ 
              color: '#1a202c', 
              fontWeight: 700, 
              marginBottom: '0.5rem',
              fontSize: '2rem',
              letterSpacing: '-0.025em',
              background: 'linear-gradient(135deg, #1a202c 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Create Account</h1>
            <p style={{ 
              color: '#64748b', 
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.5'
            }}>Join AnCore to get started</p>
          </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label htmlFor="email" style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '0.75rem',
                letterSpacing: '0.025em'
              }}>
                <i className="bi bi-envelope" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  fontWeight: 400
                }}
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                required
                autoComplete="email"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = '#fafbfc';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label htmlFor="username" style={{ 
                display: 'block', 
                fontSize: '0.875rem', 
                fontWeight: 600, 
                color: '#374151', 
                marginBottom: '0.75rem',
                letterSpacing: '0.025em'
              }}>
                <i className="bi bi-person" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                Username *
              </label>
              <input
                type="text"
                id="username"
                name="username"
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  fontWeight: 400
                }}
                placeholder="Choose a username"
                value={formData.username}
                onChange={handleInputChange}
                required
                autoComplete="username"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = '#fafbfc';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <small style={{ color: '#64748b', marginTop: '0.5rem', display: 'block', fontSize: '0.75rem' }}>
                Only letters, numbers, hyphens, and underscores allowed
              </small>
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="full_name" style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: 600, 
              color: '#374151', 
              marginBottom: '0.75rem',
              letterSpacing: '0.025em'
            }}>
              <i className="bi bi-person-badge" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
              Full Name
            </label>
            <input
              type="text"
              id="full_name"
              name="full_name"
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease',
                background: '#fafbfc',
                fontWeight: 400
              }}
              placeholder="Enter your full name (optional)"
              value={formData.full_name}
              onChange={handleInputChange}
              autoComplete="name"
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = '#fafbfc';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="password" style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: 600, 
              color: '#374151', 
              marginBottom: '0.75rem',
              letterSpacing: '0.025em'
            }}>
              <i className="bi bi-lock" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
              Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                style={{
                  width: '100%',
                  padding: '1rem 3.5rem 1rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  background: '#fafbfc',
                  fontWeight: 400
                }}
                placeholder="Create a password"
                value={formData.password}
                onChange={handleInputChange}
                required
                autoComplete="new-password"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#667eea';
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = '#fafbfc';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <button
                type="button"
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setShowPassword(!showPassword)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
            
            {formData.password && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <small style={{ color: '#64748b', fontWeight: 500, fontSize: '0.75rem' }}>Password strength:</small>
                  <small style={{ color: getPasswordStrengthColor(), fontWeight: 600, fontSize: '0.75rem' }}>
                    {getPasswordStrengthText()}
                  </small>
                </div>
                <div style={{ 
                  height: '6px', 
                  background: '#f1f5f9', 
                  borderRadius: '3px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
                }}>
                  <div
                    className="strength-indicator"
                    style={{
                      width: `${(passwordStrength / 5) * 100}%`,
                      height: '100%',
                      backgroundColor: getPasswordStrengthColor(),
                      transition: 'all 0.3s ease',
                      borderRadius: '3px'
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="confirmPassword" style={{ 
              display: 'block', 
              fontSize: '0.875rem', 
              fontWeight: 600, 
              color: '#374151', 
              marginBottom: '0.75rem',
              letterSpacing: '0.025em'
            }}>
              <i className="bi bi-lock-fill" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
              Confirm Password *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease',
                background: '#fafbfc',
                fontWeight: 400
              }}
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              autoComplete="new-password"
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = '#fafbfc';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <small style={{ 
                color: '#ef4444', 
                marginTop: '0.5rem', 
                display: 'flex',
                alignItems: 'center',
                fontWeight: 500,
                background: '#fef2f2',
                padding: '0.5rem 0.75rem',
                borderRadius: '8px',
                border: '1px solid #fecaca',
                fontSize: '0.75rem'
              }}>
                <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
                Passwords do not match
              </small>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2rem' }}>
            <input 
              type="checkbox" 
              id="terms" 
              required 
              style={{ 
                marginRight: '0.75rem',
                marginTop: '0.125rem',
                width: '18px',
                height: '18px',
                accentColor: '#667eea'
              }} 
            />
            <label htmlFor="terms" style={{ 
              fontSize: '0.875rem', 
              color: '#64748b',
              fontWeight: 500,
              lineHeight: '1.5',
              cursor: 'pointer'
            }}>
              I agree to the{' '}
              <a href="/terms-of-service" style={{
                color: '#667eea',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#5a67d8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#667eea'}
              >Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy-policy" style={{
                color: '#667eea',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#5a67d8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#667eea'}
              >Privacy Policy</a>
            </label>
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '1rem',
              background: isLoading ? '#e5e7eb' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: isLoading ? '#64748b' : 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              marginBottom: '1.5rem',
              boxShadow: isLoading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)',
              letterSpacing: '0.025em'
            }}
            disabled={isLoading}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #e5e7eb',
                  borderTop: '2px solid #64748b',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block',
                  marginRight: '0.75rem'
                }}></div>
                Creating account...
              </>
            ) : (
              <>
                <i className="bi bi-person-plus" style={{ marginRight: '0.75rem' }}></i>
                Create Account
              </>
            )}
          </button>
        </form>

        <div style={{ 
          position: 'relative', 
          textAlign: 'center', 
          marginBottom: '1.5rem',
          color: '#64748b',
          fontSize: '0.875rem',
          fontWeight: 500
        }}>
          <div style={{ 
            position: 'absolute', 
            top: '50%', 
            left: 0, 
            right: 0, 
            height: '1px', 
            background: 'linear-gradient(to right, transparent, #e5e7eb, transparent)' 
          }}></div>
          <span style={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            padding: '0 1.5rem',
            backdropFilter: 'blur(10px)'
          }}>or</span>
        </div>

        <button
          type="button"
          style={{
            width: '100%',
            padding: '1rem',
            background: 'white',
            color: '#374151',
            border: '2px solid #e5e7eb',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            letterSpacing: '0.025em'
          }}
          onClick={handleGoogleRegister}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f8fafc';
            e.currentTarget.style.borderColor = '#667eea';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
          }}
        >
          <i className="bi bi-google" style={{ marginRight: '0.75rem', color: '#ea4335' }}></i>
          Sign up with Google
        </button>

        <div style={{ textAlign: 'center' }}>
          <p style={{ 
            color: '#64748b', 
            fontSize: '0.875rem', 
            margin: 0,
            fontWeight: 500
          }}>
            Already have an account?{' '}
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                color: '#667eea',
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'color 0.2s ease'
              }}
              onClick={onSwitchToLogin}
              onMouseEnter={(e) => e.currentTarget.style.color = '#5a67d8'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#667eea'}
            >
              Sign in
            </button>
          </p>
        </div>
        </div>
      </div>
    </>
  );
};

export default Register;
